import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { usersApi, type User, type UsersQueryParams } from '../../services/usersService'
import type { Api } from '../../types/generated'

// Async thunks for API calls using proper HATEOAS navigation
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (params: UsersQueryParams = {}, { getState }) => {
    const state = getState() as any
    const usersHref = state.auth.currentClinic?.users?.href
    
    if (!usersHref) {
      throw new Error('No clinic selected or clinic missing users link')
    }
    
    console.log('HATEOAS: Following users href:', usersHref, 'with params:', params)
    
    // This is the HATEOAS way - use the href the API gave us
    const response = await usersApi.getUsers(usersHref, params)
    return response
  }
)

// Server-side search for users with real-time debouncing
export const searchUsers = createAsyncThunk(
  'users/searchUsers',
  async (searchTerm: string, { getState }) => {
    const state = getState() as any
    const usersHref = state.auth.currentClinic?.users?.href
    
    if (!usersHref) {
      throw new Error('No clinic selected or clinic missing users link')
    }
    
    console.log('HATEOAS: Searching users with term:', searchTerm)
    
    // Use server-side search with name parameter
    const params: UsersQueryParams = searchTerm.trim() 
      ? { name: [searchTerm] } 
      : {}
    
    const response = await usersApi.getUsers(usersHref, params)
    return response
  }
)

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: Api.User, { getState }) => {
    console.log('[usersSlice] createUser thunk called', { userData });
    const state = getState() as any
    const usersHref = state.auth.currentClinic?.users?.href
    
    console.log('[usersSlice] Current clinic state:', state.auth.currentClinic);
    
    if (!usersHref) {
      console.error('[usersSlice] No users href available');
      throw new Error('No clinic selected or clinic missing users link')
    }
    
    console.log('HATEOAS: Creating user at href:', usersHref)
    
    // Create user returns just id/href, so fetch full user after creation
    const createResponse = await usersApi.createUser(usersHref, userData)
    console.log('[usersSlice] Create response:', createResponse);
    
    // Fetch the full user details using the returned href
    const fullUser = await usersApi.getUser(createResponse.href)
    console.log('[usersSlice] Full user fetched:', fullUser);
    return fullUser
  }
)

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ userHref, userData }: { userHref: string; userData: Partial<Api.User> }) => {
    console.log('[usersSlice] updateUser thunk called', { userHref, userData });
    console.log('HATEOAS: Updating user at href:', userHref)
    
    // Update returns status info, so fetch full user after update
    const updateResponse = await usersApi.updateUser(userHref, userData)
    console.log('[usersSlice] Update response:', updateResponse);
    
    // Fetch the updated user details using the same href
    const updatedUser = await usersApi.getUser(userHref)
    console.log('[usersSlice] Updated user fetched:', updatedUser);
    return updatedUser
  }
)

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async ({ userId, userHref }: { userId: string; userHref: string }) => {
    console.log('HATEOAS: Deleting user at href:', userHref)
    
    await usersApi.deleteUser(userHref)
    return userId  // Return ID for state management
  }
)

// State interface
interface UsersState {
  users: User[]
  total: number
  loading: boolean
  error: string | null
  searchTerm: string
  isSearching: boolean
  // HATEOAS pagination state
  pageSize: number
  pageNumber: number
  pagePrevious: string | null
  pageNext: string | null
}

// Initial state
const initialState: UsersState = {
  users: [],
  total: 0,
  loading: false,
  error: null,
  searchTerm: '',
  isSearching: false,
  pageSize: 10,
  pageNumber: 1,
  pagePrevious: null,
  pageNext: null,
}

// Slice
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload.users
        state.total = action.payload.total
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch users'
      })

    // Create user
    builder
      .addCase(createUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false
        state.users.push(action.payload)
        state.total += 1
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create user'
      })

    // Update user
    builder
      .addCase(updateUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false
        const index = state.users.findIndex(u => u.id === action.payload.id)
        if (index !== -1) {
          state.users[index] = action.payload
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update user'
      })

    // Delete user
    builder
      .addCase(deleteUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false
        state.users = state.users.filter(u => u.id !== action.payload)
        state.total -= 1
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete user'
      })

    // Search users
    builder
      .addCase(searchUsers.pending, (state) => {
        state.isSearching = true
        state.error = null
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.isSearching = false
        state.users = action.payload.users
        state.total = action.payload.total
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.isSearching = false
        state.error = action.error.message || 'Failed to search users'
      })
  },
})

// Export actions
export const { setSearchTerm, clearError, setPageSize, setPageNumber } = usersSlice.actions

// Selectors
export const selectUsers = (state: { users: UsersState }) => state.users.users
export const selectUsersTotal = (state: { users: UsersState }) => state.users.total
export const selectUsersLoading = (state: { users: UsersState }) => state.users.loading
export const selectUsersError = (state: { users: UsersState }) => state.users.error
export const selectSearchTerm = (state: { users: UsersState }) => state.users.searchTerm
export const selectIsSearching = (state: { users: UsersState }) => state.users.isSearching
export const selectPageSize = (state: { users: UsersState }) => state.users.pageSize
export const selectPageNumber = (state: { users: UsersState }) => state.users.pageNumber
export const selectPagePrevious = (state: { users: UsersState }) => state.users.pagePrevious
export const selectPageNext = (state: { users: UsersState }) => state.users.pageNext

// Since we now use server-side search, this selector just returns the users
// The filtering is done on the server when searchUsers is called
export const selectFilteredUsers = (state: { users: UsersState }) => {
  return state.users.users
}

export default usersSlice.reducer